// Engineering BOMs — data access (docs/design/bom-views.md, ADR-043).
//
//   list      rpc engineering_bom_page(status, term, filters, sort, after, limit) —
//             one row per revision, keyset (item code, rev desc) on
//             engineering_boms_list_idx; column filters and sort server-side.
//   detail    rpc engineering_bom_document(id) — header, lines, the item's
//             revisions in one round-trip.
//   writes    save_engineering_bom / release_ / obsolete_ / delete_engineering_bom —
//             one RPC each, expected revision (PT409 on a stale one).
//   EBOM→MBOM transfer_engineering_bom(id) — a draft manufacturing BOM version;
//             compare_engineering_bom(id) — per component, read (slice 2).

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { sortParam, type EngineeringBomStatus, type ListFilter, type ListSort } from "@patronix/domain/list";
import type { Page } from "@/components/DataList";
import { isDemoMode, supabase } from "@/lib/supabase";

export type EbomFilter = "current" | EngineeringBomStatus | "all";
export const ebomFilters: EbomFilter[] = ["current", "draft", "released", "obsolete", "all"];

export interface EbomListParams {
  status: EbomFilter;
  q: string;
  filters?: ListFilter[];
  sort?: ListSort | null;
}

export interface EbomRow {
  id: string;
  item_id: string;
  item_code: string;
  item_description: string;
  rev_no: number;
  rev: string;
  status: EngineeringBomStatus;
  change_no: string | null;
  line_count: number;
  released_at: string | null;
  /** Released revisions: does the item's current MBOM come from it? */
  mbom_state: MbomState | null;
}

export type MbomState = "none" | "linked" | "behind";

export interface EbomLine {
  id: string;
  line_no: number;
  find_no: number | null;
  component_item_id: string;
  component_code: string;
  component_description: string;
  component_procurement_type: "purchase" | "manufacture";
  qty: string | number;
  uom_id: string;
  uom_code: string;
  uom_decimals: number;
  base_qty: string | number;
  base_uom_code: string;
  reference: string | null;
  note: string | null;
}

export interface EbomDocument {
  id: string;
  item_id: string;
  item_code: string;
  item_description: string;
  item_procurement_type: "purchase" | "manufacture";
  base_uom_code: string;
  rev_no: number;
  rev: string;
  status: EngineeringBomStatus;
  change_no: string | null;
  note: string | null;
  line_count: number;
  revision: number;
  released_at: string | null;
  released_by_name: string | null;
  obsoleted_at: string | null;
  created_at: string;
  lines: EbomLine[];
  mbom_state: MbomState | null;
  mbom: { id: string; version: number; status: string; engineering_bom_id: string | null; engineering_rev: string | null } | null;
  revisions: { id: string; rev: string; status: EngineeringBomStatus; released_at: string | null }[];
}

export interface EbomLineInput {
  component_item_id: string;
  qty: string;
  find_no?: number | null;
  reference?: string | null;
  note?: string | null;
}

export interface EbomSaveResult {
  id: string;
  rev: string;
  status: EngineeringBomStatus;
  revision: number;
}

export const EBOM_PAGE = 50;
const STALE = 60_000;

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

function noDemo() {
  if (isDemoMode) throw new Error("Engineering BOMs are not available in demo mode");
}

export async function listEboms(p: EbomListParams, cursor: string | null, signal: AbortSignal, size = EBOM_PAGE): Promise<Page<EbomRow>> {
  if (isDemoMode) return { rows: [], nextCursor: null };
  const { data, error } = await db()
    .rpc("engineering_bom_page", {
      p_status: p.status,
      p_term: p.q.trim() || null,
      p_filters: p.filters ?? [],
      p_sort: sortParam(p.sort ?? null),
      p_after: cursor ? JSON.parse(cursor) : null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = data as (EbomRow & { sort_cursor: unknown })[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? JSON.stringify(last.sort_cursor) : null };
}

async function getEbom(id: string, signal: AbortSignal): Promise<EbomDocument | null> {
  if (isDemoMode) return null;
  const { data, error } = await db().rpc("engineering_bom_document", { p_id: id }).abortSignal(signal);
  if (error) throw error;
  return (data as EbomDocument | null) ?? null;
}

export async function saveEbom(
  id: string | null,
  itemId: string,
  header: { change_no?: string | null; note?: string | null; expected_revision?: number },
  lines: EbomLineInput[],
): Promise<EbomSaveResult> {
  noDemo();
  const { data, error } = await db().rpc("save_engineering_bom", { p_id: id, p_item_id: itemId, p_header: header, p_lines: lines });
  if (error) throw error;
  return data as EbomSaveResult;
}

export type EbomAction = "release" | "obsolete" | "delete";

export async function ebomAction(id: string, action: EbomAction, revision: number): Promise<void> {
  noDemo();
  const fn = { release: "release_engineering_bom", obsolete: "obsolete_engineering_bom", delete: "delete_engineering_bom" }[action];
  const { error } = await db().rpc(fn, { p_id: id, p_expected_revision: revision });
  if (error) throw error;
}

export async function transferEbom(id: string): Promise<{ id: string; version: number }> {
  noDemo();
  const { data, error } = await db().rpc("transfer_engineering_bom", { p_ebom_id: id });
  if (error) throw error;
  return data as { id: string; version: number };
}

export type CompareState = "same" | "qty" | "ebom_only" | "mbom_only";
export interface EbomCompare {
  ebom_id: string;
  bom: { id: string; version: number } | null;
  rows: {
    component_item_id: string;
    component_code: string;
    component_description: string;
    base_uom_code: string;
    ebom_qty: string | number | null;
    mbom_qty: string | number | null;
    state: CompareState;
  }[];
}

async function compareEbom(id: string, signal: AbortSignal): Promise<EbomCompare | null> {
  if (isDemoMode) return null;
  const { data, error } = await db().rpc("compare_engineering_bom", { p_ebom_id: id }).abortSignal(signal);
  if (error) throw error;
  return (data as EbomCompare | null) ?? null;
}

export const ebomKeys = {
  all: ["engineering", "eboms"] as const,
  list: (p: EbomListParams) => [...ebomKeys.all, "list", p] as const,
  detail: (id: string) => [...ebomKeys.all, "detail", id] as const,
  compare: (id: string) => [...ebomKeys.all, "compare", id] as const,
};

export function ebomCompareQuery(id: string) {
  return queryOptions({ queryKey: ebomKeys.compare(id), queryFn: ({ signal }) => compareEbom(id, signal), staleTime: 30_000 });
}

export function ebomListQuery(p: EbomListParams) {
  return infiniteQueryOptions({
    queryKey: ebomKeys.list(p),
    queryFn: ({ pageParam, signal }) => listEboms(p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<EbomRow>) => last.nextCursor,
    staleTime: STALE,
  });
}

export function ebomDetailQuery(id: string) {
  return queryOptions({ queryKey: ebomKeys.detail(id), queryFn: ({ signal }) => getEbom(id, signal), staleTime: STALE });
}

const rules: [RegExp, string][] = [
  [/already has a draft revision/, "ebom.err.draftExists"],
  [/changed by someone else/, "ebom.err.conflict"],
  [/^Only a draft revision can be changed/, "ebom.err.notDraft"],
  [/^An engineering BOM needs at least one component/, "ebom.err.noLines"],
  [/^A service has no engineering BOM/, "ebom.err.service"],
  [/^A component is inactive/, "prod.err.componentInactiveLine"],
  [/is not an active manufactured item; a manufacturing BOM needs one/, "ebom.transferNotManufactured"],
  [/^Missing permission/, "ebom.err.permission"],
];

const lineRules: [RegExp, string][] = [
  [/^component and quantity are required/, "prod.err.incompleteLine"],
  [/^quantity must be above zero/, "po.err.qtyPositive"],
  [/^an item cannot be its own component/, "prod.err.selfComponent"],
  [/^services cannot be components/, "prod.err.serviceComponent"],
  [/^component is inactive/, "prod.err.componentInactiveLine"],
];

export function ebomErrorMessage(t: TFunction, error: unknown): string {
  const tr = t as unknown as (key: string, o?: Record<string, string>) => string;
  const message = (error as { message?: string } | null)?.message ?? String(error ?? "");
  const line = /^Line (\d+): (.+)$/.exec(message);
  if (line) {
    const rule = lineRules.find(([re]) => re.test(line[2] ?? ""));
    return tr("prod.err.line", { line: line[1] ?? "", message: rule ? tr(rule[1]) : (line[2] ?? "") });
  }
  if (/would contain a cycle/.test(message)) return tr("ebom.err.cycle", { path: message.split(": ").slice(1).join(": ") });
  const rule = rules.find(([re]) => re.test(message));
  return rule ? tr(rule[1]) : message || tr("auth.errorGeneric");
}
